import { cn, verifyEmail } from "@/lib/utils";
import { useDroppable } from "@dnd-kit/core";
import { X } from "lucide-react";
import { RefObject, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterInputWithSuggestions from "../FilterInputWithSuggestions";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import RecipientChip, { RecipientDragData } from "./RecipientChip";
import { RecipientField } from "./recipients";

type MultipleEmailAddressInputProps = {
    toRecipients: string[];
    setToRecipients: (toRecipients: string[]) => void;
    className?: string;
    label: string;
    focus: boolean;
    id?: string;
    toInputRef?: RefObject<HTMLInputElement | null>;
    alwaysVisible?: boolean;
    field?: RecipientField;
    getUnavailableReason?: (email: string) => string | undefined;
};

const MultipleEmailAddressInput = ({
    toRecipients,
    setToRecipients,
    label,
    className,
    focus,
    id,
    toInputRef,
    alwaysVisible,
    field,
    getUnavailableReason,
}: MultipleEmailAddressInputProps) => {
    const { t } = useTranslation();
    const [showAllEmails, setShowAllEmails] = useState(false);

    const { setNodeRef, isOver, active } = useDroppable({
        id: field ?? label,
        disabled: !field,
    });
    const dragSource = (active?.data.current as RecipientDragData | undefined)
        ?.field;
    const isDropCandidate = Boolean(field && active && dragSource !== field);
    const isDropTarget = isOver && isDropCandidate;

    const localRef = useRef<HTMLInputElement>(null);
    const toRef = toInputRef || localRef;

    const removeEmail = (emailToRemove: string) => {
        setToRecipients(
            toRecipients.filter((email) => email !== emailToRemove),
        );
    };

    const displayedEmails = showAllEmails
        ? toRecipients
        : toRecipients.slice(0, 3);
    const remainingCount = toRecipients.length - 3;
    const visible = focus || toRecipients.length > 0 || alwaysVisible;

    return (
        <div
            ref={setNodeRef}
            className={cn(
                "flex w-full items-center gap-2 rounded-lg",
                "transition-[background-color,box-shadow] duration-200 ease-out",
                focus ? "flex-wrap" : "flex-nowrap",
                visible
                    ? "[&:not(:first-child)]:mt-2"
                    : "h-0 w-0 overflow-hidden",
                isDropCandidate &&
                    "bg-surface-primary ring-4 ring-surface-primary",
                isDropTarget &&
                    "bg-surface-activeFg ring-4 ring-surface-activeFg",
                className,
            )}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setShowAllEmails(false);
                }
            }}
            onClick={() => {
                toRef.current?.focus();
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    toRef.current?.focus();
                }
            }}
            role="button"
            tabIndex={0}
        >
            <p
                className={cn(
                    "text-sm text-muted-foreground transition-colors duration-200",
                    isDropTarget && "text-primary-dark",
                )}
            >
                {label}
            </p>

            {displayedEmails.map((email) => (
                <RecipientChip
                    key={email}
                    email={email}
                    field={field}
                    onRemove={removeEmail}
                />
            ))}
            {!showAllEmails && remainingCount > 0 && (
                <Popover>
                    <PopoverTrigger asChild>
                        <Badge
                            className="cursor-pointer hover:bg-secondary/80 whitespace-nowrap"
                            onClick={() => setShowAllEmails(true)}
                            variant="default"
                        >
                            +{remainingCount} {t("common.more")}
                        </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-4">
                        <div className="flex flex-col gap-2">
                            {toRecipients.slice(3).map((email) => (
                                <div
                                    key={email}
                                    className="flex items-center justify-between"
                                >
                                    <span className="text-sm">{email}</span>
                                    <Button
                                        aria-label={t("common.remove")}
                                        className="h-6 w-6 hover:text-destructive"
                                        onClick={() => removeEmail(email)}
                                        size="tinyCircle"
                                        variant="ghost"
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </PopoverContent>
                </Popover>
            )}
            <FilterInputWithSuggestions
                ref={toRef}
                className={cn("min-w-6 w-full focus:min-w-20 leading-[27px]")}
                containerClassName="flex-1"
                getUnavailableReason={getUnavailableReason}
                id={id}
                placeholder={undefined}
                setValues={setToRecipients}
                validator={verifyEmail}
                values={toRecipients}
            />
        </div>
    );
};

export default MultipleEmailAddressInput;
